
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface BestellungData {
  bestellnummer: string;
  kunde_name?: string;
  name?: string;
  kunde_email?: string;
  email?: string;
  kunde_telefon?: string;
  telefon?: string;
  lieferadresse?: string;
  adresse?: string;
  produkte: Array<{
    id?: string;
    name: string;
    price?: number;
    preis?: number;
    quantity?: number;
    menge?: number;
    image?: string;
  }>;
  gesamtpreis: number;
  bemerkungen?: string;
  notizen?: string;
  status: string;
  datum?: string;
}

export default function BestellbestaetigungPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [bestellung, setBestellung] = useState<BestellungData | null>(null);

  useEffect(() => {
    const bestellungData = location.state?.bestellung;
    if (!bestellungData) {
      // Wenn keine Bestelldaten vorhanden, zurück zum Shop
      navigate('/shop');
      return;
    }
    setBestellung(bestellungData);
  }, [location.state, navigate]);

  if (!bestellung) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Bestelldaten werden geladen...</p>
        </div>
      </div>
    );
  }

  const kundenName = bestellung.kunde_name || bestellung.name || 'Kunde';
  const kundenEmail = bestellung.kunde_email || bestellung.email || '';
  const kundenTelefon = bestellung.kunde_telefon || bestellung.telefon || '';
  const adresse = bestellung.lieferadresse || bestellung.adresse || '';
  const bemerkungen = bestellung.bemerkungen || bestellung.notizen || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <i className="ri-check-line text-white text-xl"></i>
              </div>
              <h1 className="text-2xl font-black text-gray-900">Dä Snäckomat</h1>
            </div>
            <button
              onClick={() => navigate('/shop')}
              className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer flex items-center space-x-2"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Zurück zum Shop</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Erfolgs-Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-double-line text-3xl text-green-600"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Bestellung erfolgreich!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Vielen Dank für Ihre Bestellung. Wir haben alle Details erhalten und werden Sie bald kontaktieren.
          </p>
        </div>

        {/* Bestelldetails */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-blue-500 px-8 py-6">
            <h2 className="text-2xl font-bold text-white">Bestellbestätigung</h2>
            <p className="text-green-100 mt-2">Bestellnummer: {bestellung.bestellnummer}</p>
          </div>

          {/* Inhalt */}
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Kundendaten */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-user-line text-blue-500 mr-2"></i>
                  Kundendaten
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Name:</span>
                    <p className="text-gray-900">{kundenName}</p>
                  </div>
                  {kundenEmail && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">E-Mail:</span>
                      <p className="text-gray-900">{kundenEmail}</p>
                    </div>
                  )}
                  {kundenTelefon && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Telefon:</span>
                      <p className="text-gray-900">{kundenTelefon}</p>
                    </div>
                  )}
                  {adresse && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Adresse:</span>
                      <p className="text-gray-900">{adresse}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Bestellinfo */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-information-line text-blue-500 mr-2"></i>
                  Bestellinformationen
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Bestellnummer:</span>
                    <p className="text-gray-900 font-mono">{bestellung.bestellnummer}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Status:</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <i className="ri-time-line mr-1"></i>
                      In Bearbeitung
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Datum:</span>
                    <p className="text-gray-900">{new Date().toLocaleDateString('de-CH')}</p>
                  </div>
                  {bestellung.gesamtpreis > 0 && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Gesamtpreis:</span>
                      <p className="text-xl font-bold text-green-600">CHF {bestellung.gesamtpreis.toFixed(2)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bestellte Produkte */}
            {bestellung.produkte && bestellung.produkte.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-shopping-cart-line text-blue-500 mr-2"></i>
                  Bestellte Produkte
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    {bestellung.produkte.map((produkt, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                        <div className="flex items-center space-x-3">
                          {produkt.image && (
                            <img
                              src={produkt.image}
                              alt={produkt.name}
                              className="w-12 h-12 object-contain bg-white rounded border border-gray-200 p-1"
                            />
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{produkt.name}</p>
                            {(produkt.quantity || produkt.menge) && (
                              <p className="text-sm text-gray-500">
                                Menge: {produkt.quantity || produkt.menge}
                              </p>
                            )}
                          </div>
                        </div>
                        {(produkt.price || produkt.preis) && (
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">
                              CHF {((produkt.price || produkt.preis || 0) * (produkt.quantity || produkt.menge || 1)).toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Bemerkungen */}
            {bemerkungen && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-message-3-line text-blue-500 mr-2"></i>
                  Bemerkungen
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{bemerkungen}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nächste Schritte */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8 mb-8">
          <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center">
            <i className="ri-roadmap-line text-blue-600 mr-3"></i>
            Wie geht es weiter?
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">Bestellungseingang</h4>
                <p className="text-blue-700 text-sm">Ihre Bestellung ist bei uns eingegangen und wird geprüft.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">Kontaktaufnahme</h4>
                <p className="text-blue-700 text-sm">Wir kontaktieren Sie innerhalb von 3-5 Werktagen zur Bestätigung.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">Lieferung</h4>
                <p className="text-blue-700 text-sm">Nach Bestätigung erfolgt die Lieferung gemäß Vereinbarung.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Kontakt & Aktionen */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Kontakt */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-customer-service-2-line text-green-500 mr-3"></i>
              Fragen zur Bestellung?
            </h3>
            <div className="space-y-4">
              <a 
                href="tel:+41791234567"
                className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line text-green-600 text-xl"></i>
                <div>
                  <p className="font-medium text-gray-900">Telefon</p>
                  <p className="text-green-600">+41 79 123 45 67</p>
                </div>
              </a>
              <a 
                href="mailto:info@snaeckomat.ch"
                className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-blue-600 text-xl"></i>
                <div>
                  <p className="font-medium text-gray-900">E-Mail</p>
                  <p className="text-blue-600">info@snaeckomat.ch</p>
                </div>
              </a>
              <a 
                href="https://wa.me/41791234567"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-green-600 text-xl"></i>
                <div>
                  <p className="font-medium text-gray-900">WhatsApp</p>
                  <p className="text-green-600">Direkt chatten</p>
                </div>
              </a>
            </div>
          </div>

          {/* Aktionen */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-compass-3-line text-blue-500 mr-3"></i>
              Was möchten Sie als nächstes tun?
            </h3>
            <div className="space-y-4">
              <button
                onClick={() => navigate('/shop')}
                className="w-full flex items-center justify-center space-x-3 p-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors cursor-pointer"
              >
                <i className="ri-shopping-cart-line text-xl"></i>
                <span className="font-medium">Weiter einkaufen</span>
              </button>
              <button
                onClick={() => navigate('/bestellung')}
                className="w-full flex items-center justify-center space-x-3 p-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <i className="ri-add-circle-line text-xl"></i>
                <span className="font-medium">Neue Bestellung</span>
              </button>
              <a
                href="https://snaeckomat.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center space-x-3 p-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xl"></i>
                <span className="font-medium">Zur Hauptseite</span>
              </a>
            </div>
          </div>
        </div>

        {/* Dankesnachricht */}
        <div className="text-center mt-12 p-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl text-white">
          <h3 className="text-2xl font-bold mb-4">Vielen Dank für Ihr Vertrauen!</h3>
          <p className="text-lg opacity-90">
            Wir freuen uns auf die Zusammenarbeit und werden alles dafür tun, 
            dass Sie mit unseren Produkten und unserem Service zufrieden sind.
          </p>
        </div>
      </div>
    </div>
  );
}
